/*
 * File: DriveOverviewDialogContext.ts
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { createContext, useContext } from 'react';

import type { CoursePeriod } from 'types/periods';

/**
 * Context for the DriveOverviewDialog component.
 * Provides dialog-level data and actions to all nested components.
 *
 * @remarks
 * Components that use this context MUST be rendered within DriveOverviewDialog.
 * The context provides access to:
 * - The full course period data
 * - Dialog close function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { period, onClose } = useDriveOverviewDialogContext();
 *   const courseId = period?.course_section.course_id;
 *   // ...
 * }
 * ```
 */
export interface DriveOverviewDialogContextType {
  /**
   * Function to close the dialog
   */
  onClose: () => void;

  /**
   * The course period data for the dialog.
   * Contains full details about the period including course section,
   * instructors, enrollments, and schedule information.
   *
   * May be undefined while loading or if the period fetch fails.
   */
  period: CoursePeriod | undefined;
}

export const DriveOverviewDialogContext = createContext<DriveOverviewDialogContextType | undefined>(undefined);

/**
 * Hook to access DriveOverviewDialog context.
 *
 * @throws Error if used outside of DriveOverviewDialog
 *
 * @remarks
 * **IMPORTANT:** This hook can ONLY be used within components that are
 * rendered inside the DriveOverviewDialog component tree. Using it elsewhere
 * will throw an error.
 *
 * Components using this hook include:
 * - DialogHeader
 * - useStudentSectionData (hook)
 * - StudentInfo
 * - StudentHeader
 * - StudentActionsMenu
 *
 * @returns The dialog context containing period data and dialog actions
 *
 * @example
 * ```tsx
 * function MyDialogComponent() {
 *   const { period, onClose } = useDriveOverviewDialogContext();
 *
 *   if (!period) {
 *     return <LoadingState />;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>{period.course_section.course.title}</h1>
 *       <button onClick={onClose}>Close</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useDriveOverviewDialogContext() {
  const context = useContext(DriveOverviewDialogContext);

  if (!context) {
    throw new Error('useDriveOverviewDialogContext must be used within DriveOverviewDialog');
  }

  return context;
}